import { Component, computed, effect, inject, OnInit, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';

import { MailRecordsDialogStore } from './mail-record-dialog.store';
import { BaseDialogComponent } from 'packages/client/src/app/components/kit/base-dialog/base-dialog.component';
import { fb } from 'packages/client/src/app/modules/shared/shared.global-service';

import {
    ServiceMailsState,
    serviceMailsStateEnumOptions,
    ServiceMailsType,
    serviceMailsTypeEnumOptions,
} from 'packages/types/src/services/enum/service-mails.enums';

import { DateInputComponent } from 'packages/client/src/app/components/kit/input/date-input/date-input.component';
import { NumberInputComponent } from 'packages/client/src/app/components/kit/input/text-input/number-input.component';
import { SelectInputComponent } from 'packages/client/src/app/components/kit/input/select-input/select-input.component';
import {
    TextAreaInputComponent,
} from 'packages/client/src/app/components/kit/input/text-area-input/text-area-input.component';

import {
    ServiceMailsCreateInput,
} from '@libs/types/src/services/input-type/services/settings/service-mails.create-input';
import { checkOk } from '../../../../content.error';
import { ServiceMailsObject } from '@libs/types/src/services/object-type/service/service-mails.object';
import {
    ServiceMailsUpdateInput,
} from '@libs/types/src/services/input-type/services/settings/service-mails.update-input';
import {
    FileUploadBoxComponent,
} from 'packages/client/src/app/components/kit/file-upload-box/file-upload-box.component';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileKindEnum } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import { dateify } from '@libs/shared/utils/date.utils';
import { FormatPersonPipe } from '../../../../../shared/pipes/format-person.pipe';
import {
    MultiSelectInputComponent,
} from '../../../../../../components/kit/input/multi-select-input/multi-select-input.component';
import { UserChipComponent } from '../../../../../../components/kit/user-chip/user-chip.component';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { InfiniteScrollStore } from '../../../../../shared/store/common/infinite-scroll.store';
import { getUsersFrag } from '../../../common/select-user/select-user.query';
import { changeSignal } from '../../../../../../utils/form.utils';
import { UserChipRenderTypeEnum } from '../../../../../../components/kit/user-chip/user-chip.component.types';
import {
    InputTextAutocompleteComponent,
} from '../../../../../shared/components/inputs/input-text-autocomplete/input-text-autocomplete.component';

export type ServiceMailDialogData = {
    direction?: string
    folderId?: number
    serviceId?: number
    mailRecordData?: ServiceMailsObject
}

@Component({
    selector: 'app-mail-record-dialog',
    styleUrls: ['./mail-record-dialog.component.scss'],
    templateUrl: 'mail-record-dialog.component.html',
    providers: [
        DialogService,
        MailRecordsDialogStore,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslatePipe,
        DateInputComponent,
        NumberInputComponent,
        SelectInputComponent,
        TextAreaInputComponent,
        FileUploadBoxComponent,
        FormatPersonPipe,
        MultiSelectInputComponent,
        UserChipComponent,
        InputTextAutocompleteComponent,
    ],
})
/**
 * TODO let dialog handle it's own data and send only an ID as input
 */
export class MailRecordDialogComponent extends BaseDialogComponent implements OnInit {

    protected readonly store = inject(MailRecordsDialogStore);

    protected readonly parcelFileUploadBox = viewChild.required<FileUploadBoxComponent>('parcelFileUploadBox');
    protected readonly relatedFileUploadBox = viewChild.required<FileUploadBoxComponent>('relatedFileUploadBox');

    protected direction = 'incoming';
    protected folderId = 0;
    protected serviceId = 0;
    protected mailRecordDefaultData: ServiceMailsObject | undefined;

    protected readonly translatePrefix = 'service.mailRecords.';

    protected readonly serviceMailsTypeEnumOptions = serviceMailsTypeEnumOptions;
    protected readonly serviceMailsStateEnumOptions = serviceMailsStateEnumOptions;

    protected form = fb.group({
        receiveDate: fb.control<Date>(new Date(), Validators.required),
        sendDate: fb.control<Date>(new Date(), Validators.required),
        mailNumber: fb.control<number | null>(null, Validators.required),
        type: fb.control<string>(ServiceMailsType.ordinaryLetter, Validators.required),
        info: fb.control<string>('', { nonNullable: true }),
        recipient: fb.control<string>('', Validators.required),
        sender: fb.control<string>('', Validators.required),
        userIds: fb.control<string[]>([]),
        deadline: fb.control<Date | null>(null),
        state: fb.control<string>(ServiceMailsState.new, Validators.required),
        parcelFileId: fb.control<number | null>(null),
        relatedFileId: fb.control<number | null>(null),
        note: fb.control<string>('', { nonNullable: true }),
    });

    protected receiveDateChange = changeSignal(this.form.controls.receiveDate);
    protected sendDateChange = changeSignal(this.form.controls.sendDate);
    protected recipientChange = changeSignal(this.form.controls.recipient);
    protected senderChange = changeSignal(this.form.controls.sender);

    protected usersScroll = computed(() => {
        const filter = new FilterBuilder();
        filter.limit(100);

        if (this.serviceId) {
            filter.serviceId(this.serviceId);
        }

        return new InfiniteScrollStore(
            this.store.api.getUsers,
            filter.filter(),
            true,
            getUsersFrag,
            undefined,
            true,
        );
    });

    constructor() {
        super();
        this.applyDialogData(this.dialogConfig.data as ServiceMailDialogData | undefined);

        effect(() => {
            const name = (
                        this.direction == 'incoming'
                            ? this.senderChange()
                            : this.recipientChange()
                    )
                    || ''
            ;
            if (name.length < 2) return;

            this.store.api.getServiceMailRecipients({
                search: name,
                serviceId: this.serviceId,
            });
        });

        effect(async () => {
            if (this.mailRecordDefaultData) return;

            const date = this.direction == 'incoming'
                ? this.receiveDateChange()
                : this.sendDateChange()
            ;
            if (!date) return;

            const result = await this.store.api.getServiceMailNextNumber({
                serviceId: this.serviceId,
                date: date.toISOString(),
            });

            this.form.patchValue({ mailNumber: Number(result) });
        });
    }

    ngOnInit(): void {
        this.applyDirectionValidators(this.direction);
    }

    override async save() {
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        const payload = await this.buildPayloadForSave();

        let close = false;

        if (this.mailRecordDefaultData) {
            // UPDATE
            // Omit fields
            const { folderId, serviceId, ...omittedPayload } = payload;

            const updatePayload: ServiceMailsUpdateInput = {
                ...omittedPayload,
                id: this.mailRecordDefaultData.id,
            };

            const res = await this.update(updatePayload);
            close = res?.ok ?? false;
        } else {
            // CREATE
            const res = await this.create(payload);
            close = res?.ok ?? false;
        }

        // CONFIRM FILES
        if (payload.parcelFileId) {
            await this.parcelFileUploadBox().confirmFiles([payload.parcelFileId]);
        }
        if (payload.relatedFileId) {
            await this.relatedFileUploadBox().confirmFiles([payload.relatedFileId]);
        }

        if (close) {
            this.dialogRef.close(close);
        }
    }

    private applyDialogData(data?: ServiceMailDialogData): void {
        const predefinedMailRecord = data?.mailRecordData;

        this.direction = data?.direction ?? predefinedMailRecord?.direction ?? 'incoming';
        this.folderId = data?.folderId ?? predefinedMailRecord?.folderId ?? 0;
        this.serviceId = data?.serviceId ?? predefinedMailRecord?.serviceId ?? 0;

        if (predefinedMailRecord) {
            this.form.patchValue(this.mapRecordToForm(predefinedMailRecord));
        }

        this.mailRecordDefaultData = predefinedMailRecord;
    }

    private mapRecordToForm(record: ServiceMailsObject) {
        return {
            receiveDate: record.receiveDate ? new Date(record.receiveDate) : this.form.value.receiveDate,
            sendDate: record.sendDate ? new Date(record.sendDate) : this.form.value.sendDate,
            mailNumber: record.mailNumber ?? '',
            type: record.type ?? ServiceMailsType.ordinaryLetter,
            info: record.info ?? '',
            recipient: record.recipient ?? '',
            sender: record.sender ?? '',
            userIds: record.userIds ?? [],
            deadline: dateify(record.deadline),
            parcelFileId: record.parcelFileId ?? null,
            relatedFileId: record.relatedFileId ?? null,
            state: record.state ?? ServiceMailsState.new,
            note: record.note ?? '',
        };
    }

    private applyDirectionValidators(direction: string): void {
        const sendDate = this.form.get('sendDate');
        const receiveDate = this.form.get('receiveDate');
        const sender = this.form.get('sender');
        const recipient = this.form.get('recipient');

        sendDate?.clearValidators();
        receiveDate?.clearValidators();
        sender?.clearValidators();
        recipient?.clearValidators();

        if (direction === 'incoming') {
            // INCOMING
            receiveDate?.addValidators(Validators.required);
            sender?.addValidators(Validators.required);
        } else {
            // OUTGOING
            sendDate?.addValidators(Validators.required);
            recipient?.addValidators(Validators.required);
        }

        sendDate?.updateValueAndValidity();
        receiveDate?.updateValueAndValidity();
        sender?.updateValueAndValidity();
        recipient?.updateValueAndValidity();
    }

    private async buildPayloadForSave(): Promise<ServiceMailsCreateInput> {
        const value = this.form.value;

        const mailYear = this.getMailYear();

        const [newParcelFileIdArray, newRelatedFileIdArray] = await Promise.all([
            this.uploadFiles('parcel'),
            this.uploadFiles('related'),
        ]);

        const parcelFileId = newParcelFileIdArray.length ?
            newParcelFileIdArray[0]
            : this.form.value.parcelFileId ?? null;

        const relatedFileId = newRelatedFileIdArray.length ?
            newRelatedFileIdArray[0]
            : this.form.value.relatedFileId ?? null;

        const payload: any = {
            ...value,
            direction: this.direction,
            folderId: this.folderId,
            serviceId: this.serviceId,
            mailYear,

            info: value.info ?? '',
            note: value.note ?? null,
            mailNumber: value.mailNumber,

            receiveDate: value.receiveDate ?? null,
            sendDate: value.sendDate ?? null,
            sender: value.sender ?? '',
            recipient: value.recipient ?? '',

            userIds: value.userIds ?? [],
            state: (value.state ?? ServiceMailsState.new) as any,
            type: (value.type ?? ServiceMailsType.ordinaryLetter) as any,

            parcelFileId,
            relatedFileId,

            deadline: value.deadline ?? null,
        };

        return payload as ServiceMailsCreateInput;
    }

    private async uploadFiles(category: 'parcel' | 'related'): Promise<number[]> {
        const metadata = {
            categoryCode: DefaultCategoryTypeEnum.SERVICE_MAILS,
            note: null,
            date: new Date(),
            kind: FileKindEnum.received,
            accesses: [
                {
                    externalType: ExternalType.folder,
                    externalId: String(this.folderId),
                },
                {
                    externalType: ExternalType.service,
                    externalId: String(this.serviceId),
                },
            ],
        };

        if (category === 'parcel') {
            return this.parcelFileUploadBox().uploadFiles(metadata) || [];
        } else {
            return this.relatedFileUploadBox().uploadFiles(metadata) || [];
        }
    }

    private getMailYear(): number {
        const value = this.form.value;
        const date = this.direction === 'incoming' ? value.receiveDate : value.sendDate;

        return (date ?? new Date()).getFullYear();
    }

    private async create(data: ServiceMailsCreateInput) {
        const res = await this.store.api.createServiceMails(data);
        checkOk(res, 'create');

        return res;
    }

    private async update(data: ServiceMailsUpdateInput) {
        const res = await this.store.api.updateServiceMails(data);
        checkOk(res, 'update');

        return res;
    }

    protected readonly UserChipRenderTypeEnum = UserChipRenderTypeEnum;
}
